import { useState, type ReactElement } from "react";
import { Button } from "../../../../shared/components/ui/button";

interface ImageSourceToggleProps {
  titleId: string;
  imageType: "poster" | "backdrop" | "logo" | "horizontal-tile" | "vertical-tile";
  currentSource: string;
  tmdbUrl: string | null;
  r2Url: string | null;
  currentUrl: string | null;
  onToggle: () => void;
}

/**
 * ImageSourceToggle - Switch between TMDB and R2 image sources
 */
export function ImageSourceToggle({
  titleId,
  imageType,
  currentSource,
  tmdbUrl,
  r2Url,
  currentUrl,
  onToggle,
}: Readonly<ImageSourceToggleProps>): ReactElement | null {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Show toggle if we have both TMDB and R2 versions, or either one different from current
  const canToggle = !!(tmdbUrl || r2Url);

  console.log("ImageSourceToggle render:", {
    imageType,
    currentSource,
    tmdbUrl,
    r2Url,
    currentUrl,
    canToggle,
  });

  if (!canToggle) {
    return null;
  }

  const isTmdb = currentSource === "TMDB";
  const targetSource = isTmdb ? "R2" : "TMDB";

  // Disable toggle if trying to switch to a version that doesn't exist
  const isDisabled = (isTmdb && !r2Url) || (!isTmdb && !tmdbUrl);

  let helpText: string;
  if (isTmdb) {
    helpText = r2Url ? "Switch to custom R2 version" : "Upload a custom image to enable toggling";
  } else {
    helpText = tmdbUrl ? "Restore TMDB original" : "No TMDB version available";
  }

  const handleToggle = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("lumi-access-token");
      const endpoint = isTmdb
        ? `/v1/admin/titles/${titleId}/images/confirm-r2`
        : `/v1/admin/titles/${titleId}/images/use-tmdb`;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ imageType }),
      });

      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error || "Failed to toggle image source");
      }

      onToggle();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to toggle image source");
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    void handleToggle();
  };

  return (
    <div className="mt-2 p-3">
      <div className="flex items-center justify-between">
        <div className="text-sm">
          <p className="text-neutral-400">
            Current source: <span className="text-white font-medium">{currentSource}</span>
          </p>
          <p className="text-xs text-neutral-500 mt-1">{helpText}</p>
        </div>
        <Button onClick={handleClick} disabled={loading || isDisabled} variant="ghost-sm" size="sm" className="ml-4">
          {loading ? "Switching..." : `Use ${targetSource}`}
        </Button>
      </div>
      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
    </div>
  );
}
